/**
 * Zustand store for core game state management
 *
 * Manages turn-based game loop state including:
 * - Current player turn tracking
 * - Dice roll state and values
 * - Consecutive 6s tracking per player
 * - Player information and pawn positions
 * - Pawn movement logic and destination calculation
 * - Player ranking based on completion order
 *
 * Store design follows flat, serializable state pattern
 * to enable real-time synchronization via Ably.
 *
 * NOTE: Timer management is handled externally via gameTimerManager
 * to keep store state fully serializable.
 */

import { create } from 'zustand';
import type { PlayerInfo } from '../components/board/constants/LudoBoard.types';
import type { PawnId, PawnPosition } from '../types/positions';
import type {
  PlayerColor,
  FinalRanking,
  GameMode,
  GameEndReason,
  GameStateRedis,
  PlayerStatusSnapshot
} from 'shared-types';
import {
  START_POSITIONS,
  getPawnId,
  getPlayerIdFromPawnId,
  getPawnIndexFromPawnId
} from '../types/positions';
import { gameTimerManager } from '../utils/timerManager';
import { tryNumericToPosition } from '../utils/safe-position';
import { reportError } from '@/lib/error-reporter';
import {
  DICE_ANIMATION_DURATION_MS,
  TURN_END_DELAY_MS,
  PAWN_ANIMATION_DELAY_MS,
  AUTO_MOVE_DELAY_MS,
  MIN_DICE_SETTLE_MS
} from '../constants/timing';
import { PAWN_JUMP_DURATION_MS } from '../components/board/animations/constants';
import { calculateDestination, PLAYER_COLOR_TO_PREFIX } from './helpers/pathHelper';
import {
  isDebugDiceEnabled,
  isDebugFinalRoadEnabled,
  getDebugFinalRoadPositions
} from '../utils/debug';
import * as gameApi from '@/services/game.api';
import { getCurrentUserId } from '@/features/auth/contexts/AuthContext';

/**
 * Valid dice face values
 */
type DiceFaceValue = 1 | 2 | 3 | 4 | 5 | 6;

/**
 * Clean turn state - resets dice-related state for new player's turn
 */
const CLEAN_TURN_STATE = {
  diceValue: null,
  isRolling: false,
  waitingForPawnSelection: false,
  diceRollTimestamp: null
} as const;

/**
 * Position value indicating pawn is in base (not yet released to board)
 * Matches the server's Redis storage convention
 */
const PAWN_IN_BASE_POSITION = -1;

/**
 * Options for {@link GameActions.selectPawn}.
 */
export interface SelectPawnOptions {
  /**
   * Skip the fixed-delay `diceValue` reset that re-arms the dice after a release.
   * Server mode sets this and re-arms once it has adopted the server's post-release
   * `turnEpoch`, so the dice can never become clickable on a stale turn instance
   * (BUG-20260721-01). Offline / local mode leaves it unset — there is no response
   * to wait for.
   */
  deferDiceReset?: boolean;
}

/**
 * Game state interface
 */
interface GameState {
  // Server-authoritative turn tracking
  gameId: string | null;
  currentTurnPlayerId: string | null;
  currentTurnColor: PlayerColor | null;

  // Turn tracking (local index for UI)
  currentPlayerIndex: number;

  // Current turn instance identity (Story 04-02). Mirrors the server's
  // GameStateRedis.turnEpoch and is updated whenever server state is applied
  // wholesale via replaceStateFromServer(). Monotonic: never decrements.
  // Used to discard a resync response older than the store (see useGameSync).
  turnEpoch: number;

  // Count of inbound applies discarded for carrying a strictly-older epoch than
  // the store (Story 04-03). This is a signal, not an error: its rate is the
  // primary evidence the stale-epoch guard is doing its job. Reset per game.
  discardedEventCount: number;

  // Count of board action attempts refused because the connection was not
  // `connected` (Story 04-09). Incremented by the ViewModel's `canAct` guard
  // before it refuses to act. A signal, not an error — its rate is the primary
  // evidence the degraded-mode input block is engaging. Reset per game.
  blockedActionCount: number;

  // Count of a second, distinct roll that met the server's already-rolled guard
  // and reconciled silently (Story 04-04). Not an error: its rate is the signal
  // the roll-idempotency fix is working, so it is counted here rather than
  // routed to the crash path. Reset per game.
  alreadyRolledCount: number;

  // Count of outbound actions the server rejected with 409 STALE_TURN — a roll
  // (Story 04-04) or a move/release/advance (Story 04-05) that targeted a turn
  // instance the game already moved past. Like discardedEventCount this is a
  // signal, not a crash: the client recovers by resyncing. Every stale-turn
  // rejection across all paths funnels through the single `recordStaleTurn()`
  // action so there is exactly one counter and one mechanism. Reset per game.
  staleTurnCount: number;

  // Dice state
  diceValue: number | null;
  isRolling: boolean;
  waitingForPawnSelection: boolean;

  // Whether the roller has already applied its own current roll from the HTTP
  // response body (Story 04-04). Latched on apply and re-armed on each new
  // user-initiated roll, it makes the roller's own `dice-rolled` Ably event an
  // idempotent no-op — whichever of HTTP/Ably arrives first applies the dice,
  // the other is skipped, so exactly one dice reaches the board.
  ownRollApplied: boolean;

  // Timestamp when rollDice() was called (for computing remaining animation time)
  diceRollTimestamp: number | null;

  // Debug dice override (only active when VITE_ENABLE_DICE_DEBUG is true)
  debugDiceValue: number | null;

  // Consecutive 6s tracking (one count per player)
  consecutiveSixes: number[];

  // Player data
  players: PlayerInfo[];

  // Pawn positions
  // Using Record instead of Map for Ably serialization compatibility
  pawnPositions: Record<PawnId, PawnPosition>;

  // Animation tracking
  // Set of pawn IDs that should play animations
  animatingPawnIds: Set<PawnId>;

  // Auto-move tracking
  // Pawn ID that should be auto-clicked (triggers animation flow)
  autoMovePawnId: PawnId | null;

  // Callback for auto-release (provided by ViewModel for server-authoritative release)
  autoReleaseCallback: ((playerId: string, pawnIndex: number) => void) | null;

  // Callback that requests a coalesced reconnect resync (Story 04-03). Provided
  // by the ViewModel (which owns the active gameId + resync machinery) so the
  // store can recover from a detected desync without a static store→hook import
  // cycle. Null when no game view is mounted — an unset callback degrades to the
  // old silent-drop behaviour rather than crashing.
  resyncTriggerCallback: ((reason: string) => void) | null;

  // Player ranking state
  // Maps playerId to their finishing rank (1-4)
  playerRanks: Record<string, number>;

  // Count of players who have finished (all 4 pawns at XFINAL)
  finishedPlayerCount: number;

  // Game over state
  isGameOver: boolean;

  // Winner player ID (first place)
  winnerId: string | null;

  // Game result modal state
  showGameResultModal: boolean;

  // Final rankings for all players (from server)
  finalRankings: FinalRanking[];

  // Game mode for determining prize display
  gameMode: GameMode | null;

  // Whether this is a matchmaking game (for free game rewards)
  isMatchmaking: boolean;

  // Automove state (only visible to current user - private)
  isInAutomove: boolean;

  // Turn timeout countdown state
  turnStartedAt: number | null;
  turnTimeoutSeconds: number;

  // Spectating state (finished player watching the game)
  isSpectating: boolean;

  // Players who voluntarily left the game (their turns are skipped)
  leftPlayers: string[];

  // All-automove warning state
  allAutomoveWarningActive: boolean;
  allAutomoveEndsAt: number | null;

  // Game end reason (from server)
  gameEndReason: GameEndReason | null;
}

/**
 * Game actions interface
 */
interface GameActions {
  /**
   * Initialize players at game start
   */
  initializePlayers: (players: PlayerInfo[]) => void;

  /**
   * Set the game ID for API calls
   */
  setGameId: (gameId: string) => void;

  /**
   * Set current turn from server state
   */
  setCurrentTurn: (playerId: string, color: PlayerColor) => void;

  /**
   * Sync pawn positions from server state (Redis)
   * Converts numeric positions to string positions for each pawn
   */
  syncPawnPositionsFromServer: (serverPawnPositions: Record<string, number[]>) => void;

  /**
   * Receive dice roll from Ably event (server-authoritative)
   * Triggers animation then shows value after DICE_ANIMATION_DURATION_MS
   */
  receiveDiceRoll: (value: number, playerId: string) => void;

  /**
   * Roll the dice - calls backend API
   * Actual dice value comes from Ably event via receiveDiceRoll
   */
  rollDice: () => void;

  /**
   * Set debug dice value (only active when VITE_ENABLE_DICE_DEBUG is true)
   */
  setDebugDiceValue: (value: number | null) => void;

  /**
   * Select and release a pawn from base to start position
   */
  selectPawn: (playerId: string, pawnIndex: number, options?: SelectPawnOptions) => void;

  /**
   * Set callback for auto-release (used by server-authoritative pawn release)
   */
  setAutoReleaseCallback: (
    callback: ((playerId: string, pawnIndex: number) => void) | null
  ) => void;

  /**
   * Set the resync trigger callback (Story 04-03). The ViewModel wires this to
   * `resyncBecause(gameId, reason)` on mount and clears it on unmount.
   */
  setResyncTriggerCallback: (callback: ((reason: string) => void) | null) => void;

  /**
   * Count a board action attempt refused by the degraded-mode input block
   * (Story 04-09). Called by the ViewModel's `canAct` guard instead of running
   * the handler. Reports through the same observability path as other guard
   * trips (see `applyIfFresh`) — the rate is the evidence the block works.
   */
  incrementBlockedActionCount: () => void;

  /**
   * Increment `staleTurnCount` (Story 04-05). Called when the server rejects an
   * outbound action with 409 STALE_TURN — the move/release/advance paths and the
   * roll path (Story 04-04) all funnel through this single action. A signal for
   * observability — never an error path. The accompanying resync is requested
   * separately (via the resync trigger), so this only records the occurrence.
   */
  recordStaleTurn: () => void;

  /**
   * Move a pawn on the board based on dice value
   */
  movePawn: (
    pawnId: PawnId,
    diceValue: number,
    onMovementComplete?: (pawnId: PawnId, finalPosition: PawnPosition) => void
  ) => void;

  /**
   * End current turn and advance to next player
   */
  endTurn: () => void;

  /**
   * Reset consecutive 6s count for a specific player
   */
  resetConsecutiveSixes: (playerIndex: number) => void;

  /**
   * Calculate destination position for a pawn given current position and dice value
   * Returns null if the move would overshoot XFINAL from center corridor
   */
  calculateDestinationPosition: (
    currentPosition: PawnPosition,
    diceValue: number,
    playerId: string
  ) => PawnPosition | null;

  /**
   * Get list of clickable pawns for current player based on dice value
   * Filters out pawns that would overshoot XFINAL from center corridor
   */
  getClickablePawns: (playerId: string, diceValue: number) => PawnId[];

  /**
   * Check if player has no legal moves available
   */
  hasNoLegalMoves: (playerId: string, diceValue: number) => boolean;

  /**
   * Check if a player has completed all 4 pawns (all at XFINAL)
   */
  checkPlayerCompletion: (playerId: string) => boolean;

  /**
   * Count how many pawns a player has at XFINAL position
   */
  countPawnsAtFinal: (playerId: string) => number;

  /**
   * Set game over state with final rankings
   * Called when server broadcasts game:over event
   */
  setGameOver: (
    rankings: FinalRanking[],
    gameMode: GameMode,
    isMatchmaking?: boolean,
    endReason?: GameEndReason | null
  ) => void;

  /**
   * Close the game result modal (but keep rankings state for potential rematch)
   */
  closeGameResultModal: () => void;

  /**
   * Set automove mode (called when automove:triggered event received)
   */
  setAutomoveMode: (isAutomove: boolean) => void;

  /**
   * Set turn started info for countdown timer
   * Called when turn:started event received
   */
  setTurnStarted: (turnStartedAt: number, timeoutSeconds: number) => void;

  /**
   * Reset automove and timer state for a new game
   * Call when switching to a new gameId to prevent state pollution
   */
  resetAutomoveState: () => void;

  /**
   * Exit automove mode - calls backend API
   */
  exitAutomove: () => Promise<void>;

  /**
   * Reset all game state to initial values
   * Called when leaving a game to clear all state and avoid confusion
   */
  resetGameState: () => void;

  /**
   * Set spectating mode (finished player watching the game)
   */
  setSpectating: (isSpectating: boolean) => void;

  /**
   * Add a player to the left players list (called when player:left-game event received)
   */
  addLeftPlayer: (playerId: string) => void;

  /**
   * Set the left players list (called when syncing from initial game state)
   */
  setLeftPlayers: (playerIds: string[]) => void;

  /**
   * Set all-automove warning state
   */
  setAllAutomoveWarning: (active: boolean, endsAt: number | null) => void;

  /**
   * Apply the authoritative automove snapshot carried by a delta-only automove
   * event — `automove:triggered`, `automove:exited`, `all-automove:warning`
   * (Story 04-06, fixes defect D13). `automovePlayerIds` is the COMPLETE set of
   * players in automove at publish time (Story 01-04), so the private
   * `isInAutomove` flag is REPLACED — derived from whether the current user is in
   * the set — never toggled by a single id. Replacing (not add/remove) is what
   * lets one delivered event reconcile any number of earlier misses: a dropped
   * `automove:exited` is corrected by the next automove event that no longer
   * lists this user. Idempotent by construction. The epoch discard rule (04-03)
   * is applied by the caller via `applyIfFresh`.
   */
  applyAutomoveSnapshot: (automovePlayerIds: string[]) => void;

  /**
   * Apply the authoritative per-seat status snapshot carried by a delta-only
   * status event — `player:finished`, `player:left-game` (Story 04-06, fixes
   * defect D13). `players` is the COMPLETE set of seats at publish time (Story
   * 01-04), so `leftPlayers` is REPLACED wholesale from each seat's `left` flag
   * rather than appending one id — one delivered event reconciles any missed
   * earlier leave. Idempotent under replay. The epoch discard rule (04-03) is
   * applied by the caller via `applyIfFresh`.
   */
  applyPlayerStatusSnapshot: (players: PlayerStatusSnapshot[]) => void;

  /**
   * Replace board state wholesale from an authoritative server snapshot
   * (Story 04-02). Sets `turnEpoch` and swaps current turn, pawn positions,
   * turn timer, automove, and left-players state from `serverState`.
   *
   * Called by the reconnect resync (useGameSync) after the epoch guard has
   * confirmed the snapshot is not older than the store. Idempotent: applying
   * the same snapshot twice converges to the same state.
   */
  replaceStateFromServer: (serverState: GameStateRedis) => void;

  /**
   * Single stale-epoch guard every server-sourced writer routes through
   * (Story 04-03). Compares `incomingEpoch` against the store's `turnEpoch`:
   *
   * - `undefined` (a pre-01-03 server that does not stamp epochs): apply, and
   *   neither advance nor discard — never freeze the board against an old server.
   * - strictly older (`< turnEpoch`): **discard** — do not run `applyFn`; count
   *   and report it. A slow HTTP response or a late Ably event that lost the race
   *   must not revert newer state (defect D17).
   * - equal or newer (`>= turnEpoch`): run `applyFn`; if strictly newer, advance
   *   `turnEpoch`. Equal epochs are contemporaneous (a roll and a move share one
   *   epoch) and must still apply.
   *
   * Living inside the store means the HTTP apply sites that write from outside
   * are covered by construction — no writer can bypass the guard.
   *
   * @returns `true` if `applyFn` ran, `false` if the update was discarded.
   */
  applyIfFresh: (incomingEpoch: number | undefined, applyFn: () => void) => boolean;
}

/**
 * Combined store type
 */
type GameStore = GameState & GameActions;

/**
 * Get the FINAL position for a player color
 */
function getFinalPosition(color: 'green' | 'yellow' | 'blue' | 'red'): string {
  const prefix = PLAYER_COLOR_TO_PREFIX[color];
  return `${prefix}FINAL`;
}

/**
 * Helper function to schedule auto-click after delay
 */
function scheduleAutoClick(pawnId: PawnId, set: (state: Partial<GameState>) => void): void {
  gameTimerManager.createTimer(() => {
    set({ autoMovePawnId: pawnId, waitingForPawnSelection: true });
  }, AUTO_MOVE_DELAY_MS);
}

/**
 * Check if only one pawn is moveable and auto-move it
 * Returns true if auto-move was triggered, false otherwise
 *
 * Auto-move triggers when exactly ONE legal move exists:
 * - Single moveable board pawn (any dice value)
 * - All moveable pawns stacked on same board cell
 * - Rolling 6 with only base pawns moveable (releases index 0)
 * - Includes captures and XFINAL completions
 * - Includes 2nd consecutive 6
 *
 * Auto-move does NOT trigger when player must make a decision:
 * - Multiple pawns in different board positions
 * - Rolling 6 with both base AND board pawns moveable
 */
function tryAutoMove(
  clickablePawns: PawnId[],
  currentPlayer: PlayerInfo,
  get: () => GameStore,
  set: (state: Partial<GameState>) => void
): boolean {
  if (clickablePawns.length === 0) {
    return false;
  }

  const state = get();
  const basePawns = clickablePawns.filter((pawnId) => !(pawnId in state.pawnPositions));
  const boardPawns = clickablePawns.filter((pawnId) => pawnId in state.pawnPositions);

  // Case 1: All clickable pawns are in base (rolling 6 with only base pawns)
  if (basePawns.length > 0 && boardPawns.length === 0) {
    // Extract pawn index from first base pawn ID using shared utility
    const pawnIndex = getPawnIndexFromPawnId(basePawns[0]);

    gameTimerManager.createTimer(() => {
      // Set waitingForPawnSelection as safety fallback - if auto-release fails,
      // user can still manually click the pawn
      set({ waitingForPawnSelection: true });

      // Use the external auto-release callback if provided (for server-authoritative release)
      // Otherwise fall back to local selectPawn (for offline mode)
      const state = get();
      if (state.autoReleaseCallback) {
        state.autoReleaseCallback(currentPlayer.id, pawnIndex);
      } else {
        state.selectPawn(currentPlayer.id, pawnIndex);
      }
    }, AUTO_MOVE_DELAY_MS);
    return true;
  }

  // Case 2: Single moveable board pawn (and NO base pawns available)
  // If base pawns exist, player has a choice: release from base OR move board pawn
  if (boardPawns.length === 1 && basePawns.length === 0) {
    scheduleAutoClick(boardPawns[0], set);
    return true;
  }

  // Case 3: Multiple pawns stacked on same cell (and NO base pawns available)
  // If base pawns exist, player has a choice: release from base OR move stacked pawns
  if (boardPawns.length > 1 && basePawns.length === 0) {
    const firstPosition = state.pawnPositions[boardPawns[0]];
    const allSamePosition = boardPawns.every(
      (pawnId) => state.pawnPositions[pawnId] === firstPosition
    );

    if (allSamePosition) {
      scheduleAutoClick(boardPawns[0], set);
      return true;
    }
  }

  // Multiple moves in different positions - player must choose
  return false;
}

/**
 * Schedule turn advancement if the current user is the turn player
 * Centralized helper to eliminate code duplication
 */
function scheduleEndTurnIfCurrentUser(isCurrentUsersTurn: boolean, get: () => GameStore): void {
  if (isCurrentUsersTurn) {
    gameTimerManager.createTimer(() => {
      get().endTurn();
    }, TURN_END_DELAY_MS);
  }
}

/**
 * Process dice roll result after animation
 * Handles consecutive 6s, auto-move, and turn management
 */
function processDiceRollResult(
  rolledValue: DiceFaceValue,
  get: () => GameStore,
  set: (state: Partial<GameState>) => void,
  isCurrentUsersTurn: boolean
): void {
  const currentState = get();

  // In server-authoritative mode, skip local game logic when:
  // - Not the current user's turn (opponent watching automove/normal play)
  // - Current user is in automove (server handles moves)
  // Only run local logic when it IS user's turn and they're NOT in automove
  if (currentState.gameId && (!isCurrentUsersTurn || currentState.isInAutomove)) {
    const updatedPlayers: PlayerInfo[] = currentState.players.map((player, idx) => {
      if (idx === currentState.currentPlayerIndex) {
        return { ...player, diceValue: rolledValue };
      }
      return player;
    });
    set({ diceValue: rolledValue, isRolling: false, players: updatedPlayers });
    return;
  }

  // Update both global diceValue AND the current player's diceValue
  const updatedPlayers: PlayerInfo[] = currentState.players.map((player, idx) => {
    if (idx === currentState.currentPlayerIndex) {
      return { ...player, diceValue: rolledValue };
    }
    return player;
  });

  set({ diceValue: rolledValue, isRolling: false, players: updatedPlayers });

  // Check for no legal moves BEFORE handling consecutive 6s
  // Re-fetch state to ensure we have the latest pawnPositions after server sync
  // This prevents a race condition where the released pawn hasn't been synced yet
  const freshState = get();
  const currentPlayer = freshState.players[freshState.currentPlayerIndex];
  if (freshState.hasNoLegalMoves(currentPlayer.id, rolledValue)) {
    // Auto-skip turn when no legal moves
    const newConsecutiveSixes = [...freshState.consecutiveSixes];
    newConsecutiveSixes[freshState.currentPlayerIndex] = 0;
    set({ consecutiveSixes: newConsecutiveSixes });

    // Proactively advance turn - no point waiting for backend automove timer
    scheduleEndTurnIfCurrentUser(isCurrentUsersTurn, get);
    return;
  }

  // Handle consecutive 6s logic AFTER animation
  if (rolledValue === 6) {
    const newConsecutiveSixes = [...freshState.consecutiveSixes];
    newConsecutiveSixes[freshState.currentPlayerIndex] += 1;

    // Third consecutive 6: forfeit turn
    if (newConsecutiveSixes[freshState.currentPlayerIndex] === 3) {
      newConsecutiveSixes[freshState.currentPlayerIndex] = 0;
      set({ consecutiveSixes: newConsecutiveSixes });

      scheduleEndTurnIfCurrentUser(isCurrentUsersTurn, get);
    } else {
      set({ consecutiveSixes: newConsecutiveSixes });

      // Get clickable pawns to determine auto-move eligibility
      const clickablePawns = freshState.getClickablePawns(currentPlayer.id, 6);

      // Try auto-move if exactly one legal move exists
      if (tryAutoMove(clickablePawns, currentPlayer, get, set)) {
        // Auto-move triggered, no need to wait for manual selection
        return;
      }

      // Multiple moves available - wait for player selection
      if (clickablePawns.length > 0) {
        set({ waitingForPawnSelection: true });
      } else {
        // Safety net: no legal moves despite rolling 6 (e.g., all pawns blocked)
        scheduleEndTurnIfCurrentUser(isCurrentUsersTurn, get);
      }
    }
  } else {
    // Non-6: Reset consecutive 6s counter
    const newConsecutiveSixes = [...freshState.consecutiveSixes];
    newConsecutiveSixes[freshState.currentPlayerIndex] = 0;
    set({ consecutiveSixes: newConsecutiveSixes });

    // Get clickable pawns to determine auto-move eligibility
    const clickablePawns = freshState.getClickablePawns(currentPlayer.id, rolledValue);

    // Try auto-move if exactly one legal move exists
    if (tryAutoMove(clickablePawns, currentPlayer, get, set)) {
      // Auto-move triggered, no need to wait for manual selection
      return;
    }

    // Multiple moves available or no moves - handle accordingly
    if (clickablePawns.length > 0) {
      // Player has legal moves - wait for pawn selection
      set({ waitingForPawnSelection: true });
    } else {
      // No legal moves - auto-skip turn
      scheduleEndTurnIfCurrentUser(isCurrentUsersTurn, get);
    }
  }
}

/**
 * Generate a fresh idempotency key for one user-initiated roll (Story 04-04).
 * Prefers `crypto.randomUUID`; falls back for environments without it.
 */
function generateRollId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `roll-${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
}

/**
 * Apply the roller's own authoritative dice roll from the HTTP response body
 * (Story 04-04) instead of waiting for the `dice-rolled` Ably event.
 *
 * Idempotent against the roller's own Ably event via `ownRollApplied`: whichever
 * of the two arrives first latches the flag and drives the display through
 * `receiveDiceRoll`; the other is a no-op — so exactly one dice reaches the
 * board. Routed through `applyIfFresh` so a response that lost a race to newer
 * state is discarded rather than reverting the board; if discarded, the local
 * rolling latch is released so the button re-enables.
 */
function applyOwnRoll(
  diceValue: number,
  turnEpoch: number,
  rollerId: string | undefined,
  get: () => GameStore,
  set: (state: Partial<GameState>) => void
): void {
  if (!rollerId) {
    set({ isRolling: false, diceRollTimestamp: null });
    return;
  }

  const applied = get().applyIfFresh(turnEpoch, () => {
    if (get().ownRollApplied) {
      return;
    }
    set({ ownRollApplied: true });
    get().receiveDiceRoll(diceValue, rollerId);
  });

  if (!applied) {
    set({ isRolling: false, diceRollTimestamp: null });
  }
}

/**
 * Zustand game store
 */
export const useGameStore = create<GameStore>((set, get) => ({
  // Initial state
  gameId: null,
  currentTurnPlayerId: null,
  currentTurnColor: null,
  currentPlayerIndex: 0,
  turnEpoch: 0,
  discardedEventCount: 0,
  blockedActionCount: 0,
  alreadyRolledCount: 0,
  staleTurnCount: 0,
  ownRollApplied: false,
  diceValue: null,
  isRolling: false,
  waitingForPawnSelection: false,
  diceRollTimestamp: null,
  debugDiceValue: null,
  consecutiveSixes: [0, 0, 0, 0],
  players: [],
  pawnPositions: {},
  animatingPawnIds: new Set(),
  playerRanks: {},
  finishedPlayerCount: 0,
  isGameOver: false,
  winnerId: null,
  showGameResultModal: false,
  finalRankings: [],
  gameMode: null,
  isMatchmaking: false,
  autoMovePawnId: null,
  autoReleaseCallback: null,
  resyncTriggerCallback: null,
  isInAutomove: false,
  turnStartedAt: null,
  turnTimeoutSeconds: 30, // Default, will be updated from server
  isSpectating: false,
  leftPlayers: [],
  allAutomoveWarningActive: false,
  allAutomoveEndsAt: null,
  gameEndReason: null,

  // Actions
  initializePlayers: (players: PlayerInfo[]) => {
    // Clear any existing timers from previous game
    gameTimerManager.clearAll();

    // Use debug positions if final road debug mode is enabled
    const pawnPositions = isDebugFinalRoadEnabled() ? getDebugFinalRoadPositions() : {};

    // Don't reset gameId - it should be preserved when initializing players for a game
    // Only reset game state, not the gameId
    set({
      players,
      currentPlayerIndex: 0,
      diceValue: null,
      waitingForPawnSelection: false,
      diceRollTimestamp: null,
      ownRollApplied: false,
      consecutiveSixes: [0, 0, 0, 0],
      pawnPositions,
      animatingPawnIds: new Set(),
      playerRanks: {},
      finishedPlayerCount: 0,
      isGameOver: false,
      winnerId: null,
      showGameResultModal: false,
      finalRankings: [],
      gameMode: null,
      isMatchmaking: false,
      autoMovePawnId: null,
      currentTurnPlayerId: null,
      currentTurnColor: null,
      isInAutomove: false,
      turnStartedAt: null,
      turnTimeoutSeconds: 30,
      leftPlayers: [],
      allAutomoveWarningActive: false,
      allAutomoveEndsAt: null,
      gameEndReason: null
    });
  },

  setGameId: (gameId: string) => {
    const state = get();
    const previousGameId = state.gameId;

    // Always set gameId
    set({ gameId });

    // If gameId is changing, reset automove state
    // This prevents state from carrying over between games
    if (previousGameId !== gameId) {
      get().resetAutomoveState();
    }
  },

  setCurrentTurn: (playerId: string, color: PlayerColor) => {
    const state = get();
    // Find player index by ID
    const playerIndex = state.players.findIndex((p) => p.id === playerId);

    // Clear all pending timers when turn changes to prevent stale endTurn() calls
    // This fixes the bug where previous player's scheduled endTurn() fires after turn advances
    gameTimerManager.clearAll();

    set({
      currentTurnPlayerId: playerId,
      currentTurnColor: color,
      currentPlayerIndex: playerIndex >= 0 ? playerIndex : state.currentPlayerIndex,
      // Reset dice state for clean turn start
      ...CLEAN_TURN_STATE,
      // Also reset auto-move state to prevent stale auto-moves from previous turn
      autoMovePawnId: null
    });
  },

  /**
   * Sync pawn positions from server state (Redis)
   * Converts from server format (numeric positions per player) to client format (string positions per pawn)
   *
   * Server format: { "playerId": [52, 53, 54, 55] }
   * Client format: { "playerId-0": "GC2", "playerId-1": "GC3", ... }
   *
   * @param serverPawnPositions - Pawn positions from server (Record<playerId, number[]>)
   */
  syncPawnPositionsFromServer: (serverPawnPositions: Record<string, number[]>) => {
    const state = get();
    const pawnPositions: Record<PawnId, PawnPosition> = {};

    // Build player lookup map once (O(n) instead of O(n*m) with repeated finds)
    const playerMap = new Map(state.players.map((p) => [p.id, p]));

    // Pawns that just transitioned base → their start cell in THIS sync — a
    // release the local client did not perform optimistically (i.e. an opponent
    // or an automove release). They get the release pop so every client sees it.
    const releasedNow = new Set<PawnId>();

    // Convert server positions to client positions
    for (const [playerId, positions] of Object.entries(serverPawnPositions)) {
      // Get player from map (O(1) lookup)
      const player = playerMap.get(playerId);
      if (!player) {
        continue;
      }

      // Convert each pawn's numeric position to string position
      positions.forEach((numericPos, pawnIndex) => {
        const pawnId = getPawnId(playerId, pawnIndex);

        // Skip pawns in base (not on board)
        if (numericPos === PAWN_IN_BASE_POSITION) {
          return;
        }

        // Per-pawn guard (Story 04-07 / D16): `numericToPosition` throws on an
        // out-of-range value, and its throwing contract stays unchanged (the
        // backend depends on it to catch a genuine server-side bug). This
        // wraps only the frontend call site so one bad position skips just
        // this pawn instead of aborting reconciliation for every pawn on the
        // board. Both the error message and the reported component are
        // static (no pawn/position embedded) so several trips in one render
        // fingerprint-dedupe into a single report rather than one per pawn.
        const stringPosition = tryNumericToPosition(
          numericPos,
          player.color
        ) as PawnPosition | null;
        if (stringPosition === null) {
          reportError(
            new Error('numericToPosition guard tripped: out-of-range server position'),
            'useGameStore:syncPawnPositionsFromServer'
          );
          // Recover through the SAME coalesced, counted trigger the ViewModel
          // wires (Story 04-03) instead of a raw resync: resyncBecause counts
          // the desync signal and coalesces a burst of trips into one in-flight
          // request (Story 04-02 AC). An unset callback degrades to silent-drop.
          state.resyncTriggerCallback?.('board-position-out-of-range');
          return; // skip only this pawn; continue reconciling the rest
        }
        pawnPositions[pawnId] = stringPosition;

        // A pawn that was NOT on the board before and now sits exactly on its own
        // start cell has just been released. (A reconnect sync places pawns at
        // their real mid-board cells, never the entry cell, so this never
        // false-fires on load.) Skip ones already animating locally (own release).
        const wasOnBoard = pawnId in state.pawnPositions;
        if (
          !wasOnBoard &&
          !state.animatingPawnIds.has(pawnId) &&
          stringPosition === START_POSITIONS[player.color]
        ) {
          releasedNow.add(pawnId);
        }
      });
    }

    // Preserve positions of currently animating pawns to prevent visual jitter
    // Their final position will be set by onMovementComplete callback
    for (const animatingPawnId of state.animatingPawnIds) {
      if (animatingPawnId in state.pawnPositions) {
        pawnPositions[animatingPawnId] = state.pawnPositions[animatingPawnId];
      }
    }

    if (releasedNow.size === 0) {
      set({ pawnPositions });
      return;
    }

    // Animate the freshly released pawns, then clear each after the pop completes.
    set({
      pawnPositions,
      animatingPawnIds: new Set([...state.animatingPawnIds, ...releasedNow])
    });

    for (const releasedPawnId of releasedNow) {
      gameTimerManager.createTimer(() => {
        const currentState = get();
        set({
          animatingPawnIds: new Set(
            [...currentState.animatingPawnIds].filter((id) => id !== releasedPawnId)
          )
        });
      }, PAWN_JUMP_DURATION_MS);
    }
  },

  receiveDiceRoll: (value: number, playerId: string) => {
    const state = get();

    // Verify the dice roll is for the expected player.
    //
    // D12: a mismatch means the store's `currentTurnPlayerId` is stale. Silently
    // dropping the event (the old behaviour) re-arms the same desync for the
    // NEXT event — the client can never escape. Applying it blindly would
    // compound the error. The only correct recovery is to refetch authoritative
    // state, so request a coalesced resync via the ViewModel-provided callback
    // (which owns the gameId + resync machinery). resyncBecause coalesces, so an
    // event burst still issues a single request.
    if (state.currentTurnPlayerId && state.currentTurnPlayerId !== playerId) {
      state.resyncTriggerCallback?.('unexpected-player');
      return;
    }

    // Cast value to DiceFaceValue (server guarantees 1-6)
    const diceValue = value as DiceFaceValue;

    // Check if the current viewing user is the turn player
    const currentUserId = getCurrentUserId();
    const isCurrentUsersTurn = currentUserId === playerId;

    // Update player's diceValue to keep both in sync
    const updatedPlayers = state.players.map((player, idx) => {
      if (idx === state.currentPlayerIndex) {
        return { ...player, diceValue };
      }
      return player;
    });

    // Single set() call to prevent extra re-render that causes dice animation restart
    set({
      isRolling: true,
      diceValue,
      players: updatedPlayers
    });

    // After animation completes, process auto-move and turn logic
    // Note: diceValue is already set above, processDiceRollResult handles the rest
    // For the current user, account for time already spent animating since rollDice() tap
    let animationDelay = DICE_ANIMATION_DURATION_MS;
    if (isCurrentUsersTurn && state.diceRollTimestamp) {
      const elapsed = Date.now() - state.diceRollTimestamp;
      animationDelay = Math.max(MIN_DICE_SETTLE_MS, DICE_ANIMATION_DURATION_MS - elapsed);
    }

    gameTimerManager.createTimer(() => {
      processDiceRollResult(diceValue, get, set, isCurrentUsersTurn);
    }, animationDelay);
  },

  calculateDestinationPosition: (
    currentPosition: PawnPosition,
    diceValue: number,
    playerId: string
  ): PawnPosition | null => {
    const state = get();

    // Find player by ID to get their color
    const player = state.players.find((p) => p.id === playerId);
    if (!player) {
      throw new Error(`Player not found: ${playerId}`);
    }

    // Map player color to pawn color prefix using centralized constant
    const pawnColor = PLAYER_COLOR_TO_PREFIX[player.color];

    return calculateDestination(currentPosition, diceValue, pawnColor);
  },

  getClickablePawns: (playerId: string, diceValue: number): PawnId[] => {
    const state = get();

    // Find player to get their color for destination calculation
    const player = state.players.find((p) => p.id === playerId);
    if (!player) return [];

    const pawnColor = PLAYER_COLOR_TO_PREFIX[player.color];

    // Get board pawns (pawns that are in pawnPositions)
    // Exclude pawns that are at FINAL positions (cannot move from final destination)
    // Also exclude pawns that would overshoot XFINAL (calculateDestination returns null)
    const boardPawns = Object.keys(state.pawnPositions).filter((pawnId) => {
      if (!pawnId.startsWith(`${playerId}-`)) return false;
      const position = state.pawnPositions[pawnId];

      // Filter out pawns at FINAL positions
      if (position.endsWith('FINAL')) return false;

      // Check if this pawn can move with current dice value (not blocked by overshoot)
      try {
        const destination = calculateDestination(position, diceValue, pawnColor);
        if (destination === null) return false;
        return true;
      } catch {
        // State desync - treat pawn as non-moveable rather than crashing
        return false;
      }
    });

    // Calculate pawns in base
    const pawnsInBase: PawnId[] = [];
    for (let i = 0; i < 4; i++) {
      const pawnId = getPawnId(playerId, i);
      if (!(pawnId in state.pawnPositions)) {
        pawnsInBase.push(pawnId);
      }
    }

    // Roll 6: Both base and board pawns are clickable
    if (diceValue === 6) {
      return [...pawnsInBase, ...boardPawns];
    }

    // Roll 1-5: Only board pawns are clickable
    return boardPawns;
  },

  hasNoLegalMoves: (playerId: string, diceValue: number): boolean => {
    const clickablePawns = get().getClickablePawns(playerId, diceValue);
    return clickablePawns.length === 0;
  },

  countPawnsAtFinal: (playerId: string): number => {
    const state = get();
    const player = state.players.find((p) => p.id === playerId);
    if (!player) return 0;

    const finalPosition = getFinalPosition(player.color);

    let count = 0;
    for (let i = 0; i < 4; i++) {
      const pawnId = getPawnId(playerId, i);
      const position = state.pawnPositions[pawnId];
      if (position === finalPosition) {
        count++;
      }
    }

    return count;
  },

  checkPlayerCompletion: (playerId: string): boolean => {
    return get().countPawnsAtFinal(playerId) === 4;
  },

  movePawn: (
    pawnId: PawnId,
    diceValue: number,
    onMovementComplete?: (pawnId: PawnId, finalPosition: PawnPosition) => void
  ) => {
    const state = get();
    // Extract playerId from pawnId using utility function
    const playerId = getPlayerIdFromPawnId(pawnId);
    const currentPlayer = state.players[state.currentPlayerIndex];

    // Validate pawn belongs to current player
    if (currentPlayer.id !== playerId) {
      return;
    }

    // Get current position (guaranteed to exist for clickable pawns)
    const currentPosition = state.pawnPositions[pawnId];

    // Calculate destination (may return null if overshoot from corridor)
    let destination: PawnPosition | null;
    try {
      destination = get().calculateDestinationPosition(currentPosition, diceValue, playerId);
    } catch {
      return;
    }

    // If destination is null (blocked by overshoot), do not move
    // This is a defensive check - should already be filtered by getClickablePawns
    if (destination === null) {
      return;
    }

    // Update position immediately (animation hook will handle visual transition)
    const newPawnPositions = {
      ...state.pawnPositions,
      [pawnId]: destination
    };

    set({
      pawnPositions: newPawnPositions,
      waitingForPawnSelection: false,
      animatingPawnIds: new Set([...state.animatingPawnIds, pawnId])
    });

    // Call completion callback if provided
    if (onMovementComplete) {
      onMovementComplete(pawnId, destination);
    }

    // Check if pawn just arrived at XFINAL - grant bonus roll
    // Note: Ranking/game-end logic is handled by usePawnMovement hook's onMovementComplete
    if (destination.endsWith('FINAL')) {
      // ALWAYS grant bonus roll when reaching XFINAL (unconditional)
      // This supersedes the roll-6 bonus (does not stack)
      // Reset consecutive 6s counter since bonus roll is being granted
      const newConsecutiveSixes = [...state.consecutiveSixes];
      newConsecutiveSixes[state.currentPlayerIndex] = 0;
      set({ consecutiveSixes: newConsecutiveSixes });

      // Grant bonus roll by resetting dice value
      gameTimerManager.createTimer(() => {
        set({ diceValue: null });
      }, PAWN_ANIMATION_DELAY_MS);
      return;
    }

    // Handle turn end logic based on dice value
    if (diceValue !== 6) {
      // Non-6: End turn after delay
      const newConsecutiveSixes = [...state.consecutiveSixes];
      newConsecutiveSixes[state.currentPlayerIndex] = 0;
      set({ consecutiveSixes: newConsecutiveSixes });

      gameTimerManager.createTimer(() => {
        get().endTurn();
      }, TURN_END_DELAY_MS);
    } else {
      // Rolled 6: Keep turn, reset dice to allow another roll
      gameTimerManager.createTimer(() => {
        set({ diceValue: null });
      }, PAWN_ANIMATION_DELAY_MS);
    }
  },

  setDebugDiceValue: (value: number | null) => {
    set({ debugDiceValue: value });
  },

  setAutoReleaseCallback: (callback: ((playerId: string, pawnIndex: number) => void) | null) => {
    set({ autoReleaseCallback: callback });
  },

  setResyncTriggerCallback: (callback: ((reason: string) => void) | null) => {
    set({ resyncTriggerCallback: callback });
  },

  incrementBlockedActionCount: () => {
    set({ blockedActionCount: get().blockedActionCount + 1 });
    reportError(new Error('degraded-mode-action-blocked'), 'game-store');
  },

  recordStaleTurn: () => {
    set({ staleTurnCount: get().staleTurnCount + 1 });
  },

  rollDice: () => {
    const state = get();

    // Block dice roll if game is over
    if (state.isGameOver) {
      return;
    }

    // Block dice roll if spectating (finished player watching the game)
    if (state.isSpectating) {
      return;
    }

    // Block dice roll if in automove mode - server is playing on our behalf
    if (state.isInAutomove) {
      return;
    }

    // If gameId is set, use server-authoritative dice roll
    if (state.gameId) {
      // Gate the button on this request's own lifecycle (Story 04-04): isRolling
      // stays true from the moment the request is issued until it resolves — the
      // 200 body, a 409 reconcile, or a failure. The value now arrives in the
      // response, so there is no more 5s safety timer: nothing left to wait out.
      // Re-arm ownRollApplied so this roll's first-arriving path (HTTP or Ably)
      // applies and the other is an idempotent no-op.
      set({
        isRolling: true,
        diceRollTimestamp: Date.now(),
        diceValue: null,
        ownRollApplied: false
      });

      // Generate the idempotency key ONCE per user-initiated roll. The request
      // object is built here and reused verbatim by 04-01's 401-refresh replay,
      // so the same rollId rides along — the whole point of an idempotency key.
      const rollId = generateRollId();
      const expectedTurnEpoch = state.turnEpoch;
      const debugValue =
        isDebugDiceEnabled() && state.debugDiceValue !== null ? state.debugDiceValue : undefined;
      const rollerId = state.currentTurnPlayerId ?? getCurrentUserId();

      gameApi
        .rollDice(state.gameId, { rollId, expectedTurnEpoch, debugDiceValue: debugValue })
        .then((outcome) => {
          switch (outcome.status) {
            case 'rolled':
              // Apply the authoritative body directly — the roller no longer
              // waits for its own dice-rolled Ably event to learn its value.
              applyOwnRoll(outcome.diceValue, outcome.turnEpoch, rollerId, get, set);
              break;
            case 'already-rolled':
              // Count the reconcile (its rate is the signal the fix works), then
              // converge to the authoritative value silently — no toast, no
              // second dice on the board.
              set({ alreadyRolledCount: get().alreadyRolledCount + 1 });
              applyOwnRoll(outcome.diceValue, outcome.turnEpoch, rollerId, get, set);
              break;
            case 'stale-turn':
              // The turn moved past us. Count it and resync to authoritative
              // state through the sanctioned trigger — no crash, no toast.
              // Route through the single recordStaleTurn() action (Story 04-05)
              // so the roll path and the move/release/advance paths share one
              // counter and one mechanism.
              get().recordStaleTurn();
              set({ isRolling: false, diceRollTimestamp: null });
              get().resyncTriggerCallback?.('roll-stale-turn');
              break;
            case 'third-consecutive-six':
              // Leave isRolling set: the backend already broadcast dice-rolled(6)
              // over Ably, and that event drives the display and the
              // post-animation forfeit via receiveDiceRoll → processDiceRollResult.
              break;
            case 'not-your-turn':
              // Server says it is not our turn — release the button silently.
              set({ isRolling: false, diceRollTimestamp: null });
              break;
          }
        })
        .catch(() => {
          // Unexpected failure (network / timeout / 500). Release the button so
          // the player can retry; do not surface a toast or route to the crash
          // path — an expected rejection would already be a handled outcome.
          set({ isRolling: false, diceRollTimestamp: null });
        });
      return;
    }

    // Fallback: Local dice roll for offline/debug mode
    // Use debug dice value if enabled and set, otherwise use random value
    const rolledValue = (
      isDebugDiceEnabled() && state.debugDiceValue !== null
        ? state.debugDiceValue
        : Math.floor(Math.random() * 6) + 1
    ) as DiceFaceValue;

    // Set dice value IMMEDIATELY for movement calculations (same as receiveDiceRoll)
    // Also update player's diceValue to keep both in sync
    const updatedPlayers = state.players.map((player, idx) => {
      if (idx === state.currentPlayerIndex) {
        return { ...player, diceValue: rolledValue };
      }
      return player;
    });

    set({
      diceValue: rolledValue,
      isRolling: true,
      players: updatedPlayers
    });

    // After animation completes, process auto-move and turn logic
    // In offline mode, the rolling player is always the current user
    gameTimerManager.createTimer(() => {
      processDiceRollResult(rolledValue, get, set, true);
    }, DICE_ANIMATION_DURATION_MS);
  },

  selectPawn: (playerId: string, pawnIndex: number, options: SelectPawnOptions = {}) => {
    const state = get();
    const currentPlayer = state.players[state.currentPlayerIndex];

    if (currentPlayer.id !== playerId) {
      return;
    }

    // Check if this is a roll 6 scenario where we could move a board pawn instead
    const pawnId = getPawnId(playerId, pawnIndex);
    if (state.diceValue === 6 && pawnId in state.pawnPositions) {
      // Pawn is already on board - move it instead of releasing from base
      get().movePawn(pawnId, 6);
      return;
    }

    // OPTIMISTIC UPDATE: Immediately update UI (for responsiveness)
    // The server-authoritative update will be handled via handlePawnClick in useGamePageViewModel
    // which calls the API and syncs via Ably for other clients

    // Add pawn to start position using utility function
    const startPosition = START_POSITIONS[currentPlayer.color];
    const newPawnPositions = {
      ...state.pawnPositions,
      [pawnId]: startPosition
    };

    // Calculate updated pawnsInBase count from positions
    const playerPawnsOnBoard = Object.keys(newPawnPositions).filter((id) =>
      id.startsWith(`${playerId}-`)
    ).length;
    const pawnsInBase = 4 - playerPawnsOnBoard;

    // Update player with calculated pawnsInBase
    const updatedPlayers = state.players.map((player, idx) => {
      if (idx === state.currentPlayerIndex) {
        return {
          ...player,
          pawnsInBase
        };
      }
      return player;
    });

    // Optimistically animate the release pop for immediate local feedback. The
    // pawn sits on its start cell, so the board plays the full-flip release
    // animation. (Opponents get the same pop via syncPawnPositionsFromServer,
    // which detects the base→board transition — so every client sees it.)
    set({
      players: updatedPlayers,
      pawnPositions: newPawnPositions,
      waitingForPawnSelection: false,
      animatingPawnIds: new Set([...state.animatingPawnIds, pawnId])
    });

    // After animation completes, remove pawn from animating set
    gameTimerManager.createTimer(() => {
      const currentState = get();
      set({
        animatingPawnIds: new Set([...currentState.animatingPawnIds].filter((id) => id !== pawnId))
      });
    }, PAWN_JUMP_DURATION_MS);

    // After releasing a pawn with a 6, reset dice value to allow another roll
    // Player keeps their turn and consecutive 6s counter continues.
    //
    // In server mode the caller defers this (BUG-20260721-01): clearing diceValue
    // is what makes the dice clickable again, and doing it on a fixed timer that
    // started at click time races the release response. If the round trip is
    // slower than the animation, the dice re-arms while the store still holds the
    // pre-release turn instance and the bonus roll goes out stale. The release
    // ViewModel re-arms instead once it has adopted the server's epoch. Offline /
    // local mode has no response to wait for, so it keeps the timer.
    if (!options.deferDiceReset) {
      gameTimerManager.createTimer(() => {
        set({ diceValue: null });
      }, PAWN_ANIMATION_DELAY_MS);
    }
  },

  endTurn: () => {
    const state = get();

    // Call backend API to advance turn server-side
    // The turn-advanced event will update all clients' state
    const currentGameId = state.gameId;
    if (currentGameId) {
      // Read the epoch we are acting on from this same snapshot (Story 04-05):
      // advance-turn (e.g. fired on no-legal-moves) must carry the turn instance
      // it observed, not a value a racing event may have advanced between here
      // and the async call.
      const epochAtAction = state.turnEpoch;

      // Import dynamically to avoid circular dependency
      import('@/services/game.api')
        .then(({ advanceTurn }) => advanceTurn(currentGameId, epochAtAction))
        .then((result) => {
          // 409 STALE_TURN (Story 04-05): the turn already advanced under us.
          // Count the rejection and resync through the sanctioned trigger — no
          // error surfaced. resyncTriggerCallback is the only store-level resync
          // path (mirrors the 04-03 desync recovery), coalesced + counted.
          if (result.staleTurn) {
            get().recordStaleTurn();
            get().resyncTriggerCallback?.('stale-turn-advance');
          }
        })
        .catch((error: unknown) => {
          if (import.meta.env.DEV) {
            console.error('Failed to advance turn:', error);
          }
        });

      // Only reset dice state - wait for server's turn-advanced event for actual turn change
      // The hardcoded % 4 was skipping inactive players incorrectly
      set({
        diceValue: null,
        isRolling: false,
        waitingForPawnSelection: false
      });
      return;
    }

    // Fallback: Local turn advancement for offline/debug mode
    const nextPlayerIndex = (state.currentPlayerIndex + 1) % state.players.length;
    const nextPlayer = state.players[nextPlayerIndex];
    const nextPlayerId = nextPlayer.id;
    const nextPlayerColor = nextPlayer.color;

    set({
      currentPlayerIndex: nextPlayerIndex,
      currentTurnPlayerId: nextPlayerId,
      currentTurnColor: nextPlayerColor,
      diceValue: null,
      isRolling: false,
      waitingForPawnSelection: false
    });
  },

  resetConsecutiveSixes: (playerIndex: number) => {
    const state = get();
    const newConsecutiveSixes = [...state.consecutiveSixes];
    newConsecutiveSixes[playerIndex] = 0;
    set({ consecutiveSixes: newConsecutiveSixes });
  },

  setGameOver: (
    rankings: FinalRanking[],
    gameMode: GameMode,
    isMatchmaking = false,
    endReason: GameEndReason | null = null
  ) => {
    // Set winnerId from first place (rankings[0])
    const winnerId = rankings.length > 0 ? rankings[0].playerId : null;

    set({
      isGameOver: true,
      showGameResultModal: true,
      finalRankings: rankings,
      gameMode,
      isMatchmaking,
      winnerId,
      gameEndReason: endReason,
      // Clear all-automove warning on game end
      allAutomoveWarningActive: false,
      allAutomoveEndsAt: null,
      // Reset dice state so players can't interact with dice after game ends
      diceValue: null,
      isRolling: false,
      waitingForPawnSelection: false
    });
  },

  closeGameResultModal: () => {
    // Only hide modal, keep other game-over state for potential rematch
    set({ showGameResultModal: false });
  },

  setAutomoveMode: (isAutomove: boolean) => {
    set({ isInAutomove: isAutomove });
  },

  setTurnStarted: (turnStartedAt: number, timeoutSeconds: number) => {
    set({
      turnStartedAt,
      turnTimeoutSeconds: timeoutSeconds
    });
  },

  resetAutomoveState: () => {
    set({
      isInAutomove: false,
      turnStartedAt: null,
      turnTimeoutSeconds: 30
    });
  },

  exitAutomove: async () => {
    const { gameId } = get();
    if (!gameId) {
      return;
    }

    // Optimistically clear automove - server will correct via turn:started if wrong
    set({ isInAutomove: false });

    try {
      await gameApi.exitAutomove(gameId);
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error('Failed to exit automove mode:', error);
      }
      // Keep isInAutomove false - user intent is clear, server will reconcile
    }
  },

  resetGameState: () => {
    // Clear all timers from the game
    gameTimerManager.clearAll();

    // Reset all state to initial values
    set({
      gameId: null,
      currentTurnPlayerId: null,
      currentTurnColor: null,
      currentPlayerIndex: 0,
      // A new game's server state starts at epoch 0; a leaked epoch leaves the
      // client permanently ahead, 409-ing every roll and discarding every event.
      turnEpoch: 0,
      discardedEventCount: 0,
      blockedActionCount: 0,
      alreadyRolledCount: 0,
      staleTurnCount: 0,
      ownRollApplied: false,
      diceValue: null,
      isRolling: false,
      waitingForPawnSelection: false,
      diceRollTimestamp: null,
      debugDiceValue: null,
      consecutiveSixes: [0, 0, 0, 0],
      players: [],
      pawnPositions: {},
      animatingPawnIds: new Set(),
      playerRanks: {},
      finishedPlayerCount: 0,
      isGameOver: false,
      winnerId: null,
      showGameResultModal: false,
      finalRankings: [],
      gameMode: null,
      isMatchmaking: false,
      autoMovePawnId: null,
      autoReleaseCallback: null,
      resyncTriggerCallback: null,
      isInAutomove: false,
      turnStartedAt: null,
      turnTimeoutSeconds: 30,
      isSpectating: false,
      leftPlayers: [],
      allAutomoveWarningActive: false,
      allAutomoveEndsAt: null,
      gameEndReason: null
    });
  },

  setSpectating: (isSpectating: boolean) => {
    set({ isSpectating });
  },

  addLeftPlayer: (playerId: string) => {
    const state = get();
    if (!state.leftPlayers.includes(playerId)) {
      set({ leftPlayers: [...state.leftPlayers, playerId] });
    }
  },

  setLeftPlayers: (playerIds: string[]) => {
    set({ leftPlayers: playerIds });
  },

  setAllAutomoveWarning: (active: boolean, endsAt: number | null) => {
    set({ allAutomoveWarningActive: active, allAutomoveEndsAt: endsAt });
  },

  applyAutomoveSnapshot: (automovePlayerIds: string[]) => {
    // Derive the private flag from the authoritative set (mirrors the
    // reconnect apply in replaceStateFromServer). Only the current user's
    // automove state is client-visible, so no per-opponent set is stored.
    const currentUserId = getCurrentUserId();
    set({ isInAutomove: currentUserId ? automovePlayerIds.includes(currentUserId) : false });
  },

  applyPlayerStatusSnapshot: (players: PlayerStatusSnapshot[]) => {
    // Replace the left-players list wholesale from the snapshot. `left` is the
    // only cross-seat status these events drive into the store; ranks are
    // reconciled through authoritative pawn-position sync + local completion.
    set({ leftPlayers: players.filter((player) => player.left).map((player) => player.playerId) });
  },

  applyIfFresh: (incomingEpoch: number | undefined, applyFn: () => void): boolean => {
    const { turnEpoch } = get();

    // Pre-01-03 server: no epoch stamped. Apply as today — treating a missing
    // epoch as 0 would freeze the board against an old server during rollout.
    if (incomingEpoch === undefined) {
      applyFn();
      return true;
    }

    // Strictly older: discard. A slow HTTP response or a late event that lost
    // the race must not revert newer state (D17). Count + report the discard as
    // a signal — its rate is the primary evidence this guard is working. The
    // reporter never throws, so observability can never block or crash an apply.
    if (incomingEpoch < turnEpoch) {
      set({ discardedEventCount: get().discardedEventCount + 1 });
      reportError(new Error('stale-epoch-discard'), 'game-store');
      return false;
    }

    // Equal or newer: apply. Equal epochs are contemporaneous (turn:started,
    // dice-rolled and pawn-moved share one epoch) and must still apply. Advance
    // the monotonic epoch only when strictly newer.
    applyFn();
    if (incomingEpoch > turnEpoch) {
      set({ turnEpoch: incomingEpoch });
    }
    return true;
  },

  replaceStateFromServer: (serverState: GameStateRedis) => {
    const state = get();

    // Wholesale swap of server-authoritative board state. Each granular action
    // is reused so this stays consistent with the initial-load / heartbeat
    // paths rather than duplicating their apply logic.
    state.syncPawnPositionsFromServer(serverState.pawnPositions);
    state.setCurrentTurn(serverState.currentTurnPlayerId, serverState.currentTurnColor);

    if (serverState.turnStartedAt) {
      state.setTurnStarted(
        serverState.turnStartedAt,
        serverState.turnTimeoutSeconds ?? state.turnTimeoutSeconds
      );
    }

    const currentUserId = getCurrentUserId();
    state.setAutomoveMode(
      currentUserId ? (serverState.automovePlayers?.includes(currentUserId) ?? false) : false
    );
    state.setLeftPlayers(serverState.leftPlayers ?? []);

    // Record the applied epoch last so a concurrent resync compares against the
    // fully-applied snapshot. The server normalizes pre-epoch states to 0.
    set({ turnEpoch: serverState.turnEpoch });
  }
}));
